// FTR F19 — unified document table (pending + exported)

import {useMemo, useCallback} from 'react'
import {Icon} from '@ui/lib/primitive/Icon'
import {useToast} from '@ui/lib/toast'
import type {ParsedSheet} from '@ts/mod/excel_gen/field_sheet_parse'
import {str, pending_doc_net_total, type PendingDoc} from '@ts/mod/ftr_f19/basic/csa_flow/core'
import type {ExportedDoc, GeneratedDoc, HighlightTarget} from './types'
import {fmt_amount, generate_export_batch_buffer} from './utils'
import {IconButton} from './IconButton'

// --

type DocTableProps = {
	pending: PendingDoc[]
	exported: ExportedDoc[]
	generatedResults: GeneratedDoc[]
	selected: Set<string>
	onToggleSelect: (short: string) => void
	onToggleSelectAll: () => void
	onPreviewPdf: (doc: PendingDoc | GeneratedDoc, sheets?: ParsedSheet[]) => void
	onOpenExcel: (sheet?: string, highlight?: HighlightTarget) => void
	onOpenExcelBuffer: (buffer: ArrayBuffer, filename: string) => void
	onGenerate: () => void
	onGenerateOne: (doc: PendingDoc) => void
	onDownloadZip: () => void
	sheets?: ParsedSheet[]
	isDone: boolean
	isParsed: boolean
}

export function DocTable({
	pending, exported, generatedResults, selected,
	onToggleSelect, onToggleSelectAll,
	onPreviewPdf, onOpenExcel, onOpenExcelBuffer,
	onGenerate, onGenerateOne, onDownloadZip,
	sheets, isDone, isParsed,
}: DocTableProps) {
	const toast = useToast()
	const pendingShorts = useMemo(() => pending.map(d => d.short), [pending])
	const generatedMap = useMemo(() => new Map(generatedResults.map(r => [r.short, r])), [generatedResults])
	const allChecked = selected.size === pendingShorts.length && pendingShorts.length > 0

	const handleCopyTsv = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		const docs = generatedResults.length > 0 ? generatedResults : []
		if (docs.length === 0) return
		const now_iso = new Date().toISOString().slice(0, 10)
		const tsv_header = 'doc\texported_at\tauthor\ttotal_amount\tccy\tline_count\tfile_name'
		const tsv_rows = docs.map(r =>
			`${r.short}\t${now_iso}\t${r.author}\t${Math.round(r.net_total * 100) / 100}\t${r.ccy}\t${r.line_count}\t${r.short}.pdf`,
		)
		navigator.clipboard.writeText([tsv_header, ...tsv_rows].join('\n'))
		toast.show({text: 'Kopierad till urklipp', variant: 'pop', event: e})
	}, [generatedResults, toast])

	// header action buttons (rendered in last <th>)
	const headerActions = (
		<div style={{display: 'flex', gap: 3, justifyContent: 'flex-end'}}>
			{selected.size > 0 && isParsed && (
				<IconButton
					icon={<Icon icon="tabler:file-export" size={16} />}
					title={`Generera ${selected.size} PDF${selected.size > 1 ? ':er' : ''}`}
					onClick={onGenerate}
					variant="outline"
					size="md"
				/>
			)}
			{isDone && generatedResults.length > 0 && (
				<>
					<IconButton
						icon={<Icon icon="tabler:clipboard-copy" size={16} />}
						title="Kopiera TSV för direkt inklistring i Excel"
						onClick={handleCopyTsv}
						variant="outline"
						size="md"
					/>
					<IconButton
						icon={<Icon icon="tabler:table" size={16} />}
						title="Förhandsgranska export-batch"
						onClick={async () => {
							const buf = await generate_export_batch_buffer(generatedResults)
							onOpenExcelBuffer(buf, 'export_batch.xlsx')
						}}
						variant="outline"
						size="md"
					/>
					<IconButton
						icon={<Icon icon="tabler:download" size={16} />}
						title="Ladda ner ZIP"
						onClick={onDownloadZip}
						variant="outline"
						size="md"
					/>
				</>
			)}
		</div>
	)

	return (
		<>
			{/* title */}
			<h3 style={{fontSize: 14, fontWeight: 600, margin: 0, color: '#444'}}>
				Dokument ({pending.length} väntande{exported.length > 0 ? `, ${exported.length} exporterade` : ''})
			</h3>

			{/* table */}
			{pending.length === 0 && exported.length === 0 ? (
				<div style={{background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 8, padding: '12px 16px', fontSize: 14}}>Inga dokument hittades.</div>
			) : (
				<table style={{width: '100%', borderCollapse: 'collapse', fontSize: 13}}>
					<thead>
						<tr>
							<th style={{...thStyle, width: 32}}>
								<input type="checkbox" checked={allChecked} onChange={onToggleSelectAll} />
							</th>
							<th style={thStyle}>Dok.nr</th>
							<th style={thStyle}>Typ</th>
							<th style={thStyle}>Mottagare</th>
							<th style={thStyle}>Rader</th>
							<th style={{...thStyle, textAlign: 'right'}}>Netto</th>
							<th style={thStyle}>Val.</th>
							<th style={thStyle}>Exporterad</th>
							<th style={{...thStyle, width: 100, textAlign: 'right'}}>{headerActions}</th>
						</tr>
					</thead>
					<tbody>
						{pending.map(d => (
							<PendingRow
								key={d.short}
								doc={d}
								gen={generatedMap.get(d.short)}
								checked={selected.has(d.short)}
								onToggle={() => onToggleSelect(d.short)}
								onPreview={(gen) => gen ? onPreviewPdf(gen) : onPreviewPdf(d, sheets)}
								onGenerate={() => onGenerateOne(d)}
								onOpenExcel={onOpenExcel}
								isParsed={isParsed}
							/>
						))}
						{exported.map(d => <ExportedRow key={d.short} doc={d} />)}
					</tbody>
				</table>
			)}

			{/* batch copy */}
			{isDone && generatedResults.length > 0 && (
				<BatchCopyBox results={generatedResults} />
			)}
		</>
	)
}

// --

const thStyle = {textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #e5e5e5', fontWeight: 600, fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.03em'} as const
const tdStyle = {padding: '6px 8px', verticalAlign: 'middle'} as const
const trStyle = {borderBottom: '1px solid #f0f0f0'} as const
const monoStyle = {fontFamily: 'SF Mono, Menlo, monospace', fontSize: 12} as const
const dimStyle = {color: '#888', fontSize: 12} as const

function PendingRow({doc, gen, checked, onToggle, onPreview, onGenerate, onOpenExcel, isParsed}: {
	doc: PendingDoc
	gen?: GeneratedDoc
	checked: boolean
	onToggle: () => void
	onPreview: (gen?: GeneratedDoc) => void
	onGenerate: () => void
	onOpenExcel: (sheet?: string, highlight?: HighlightTarget) => void
	isParsed: boolean
}) {
	const authorName = str(doc.doc_row['payout_doc.author'])
	const lineRows = doc.lines.map(l => l._row_index).filter((r): r is number => r != null)

	return (
		<tr style={trStyle}>
			<td style={tdStyle}>
				<input type="checkbox" checked={checked} onChange={onToggle} />
			</td>
			<td style={{...tdStyle, cursor: 'pointer'}} onClick={() => onOpenExcel('Utbetalningar', {sheet: 'Utbetalningar', column: 'payout_doc.short', value: doc.short})}>
				<span style={monoStyle}>{doc.short}</span>
			</td>
			<td style={{...tdStyle, cursor: 'pointer'}} onClick={() => onOpenExcel('Dok.typer', {sheet: 'Dok.typer', column: 'doc_seq_typ.short', value: doc.seq_typ})}>
				{doc.seq_typ}
			</td>
			<td style={{...tdStyle, cursor: 'pointer'}} onClick={() => onOpenExcel('Kontakter', {sheet: 'Kontakter', column: 'party.name_norm', value: authorName})}>
				{authorName}
			</td>
			<td style={{...tdStyle, cursor: 'pointer'}} onClick={() => onOpenExcel('Utbetalningsrader', {sheet: 'Utbetalningsrader', rows: lineRows})}>
				{doc.lines.length}
			</td>
			<td style={{...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
				{fmt_amount(pending_doc_net_total(doc))}
			</td>
			<td style={tdStyle}>{str(doc.doc_row['payout_doc.ccy']) || 'SEK'}</td>
			<td style={tdStyle}>{gen ? <span style={dimStyle}>just nu</span> : ''}</td>
			<td style={tdStyle}>
				<div style={{display: 'flex', gap: 2, justifyContent: 'flex-end'}}>
					{isParsed && !gen && (
						<IconButton icon={<Icon icon="tabler:file-export" size={16} />} title="Generera PDF" onClick={onGenerate} />
					)}
					<IconButton icon={<Icon icon="tabler:eye" size={16} />} title="Förhandsgranska PDF" onClick={() => onPreview(gen)} />
					{gen && (
						<IconButton icon={<Icon icon="tabler:download" size={16} />} title="Ladda ner PDF" onClick={() => {
							const blob = new Blob([gen.pdf_bytes], {type: 'application/pdf'})
							const url = URL.createObjectURL(blob)
							const a = document.createElement('a')
							a.href = url; a.download = `${gen.short}.pdf`; a.click()
							URL.revokeObjectURL(url)
						}} />
					)}
				</div>
			</td>
		</tr>
	)
}

function ExportedRow({doc}: {doc: ExportedDoc}) {
	return (
		<tr style={{...trStyle, opacity: 0.45}}>
			<td style={tdStyle}></td>
			<td style={tdStyle}><span style={monoStyle}>{doc.short}</span></td>
			<td style={tdStyle}></td>
			<td style={tdStyle}>{doc.author}</td>
			<td style={tdStyle}>{doc.line_count}</td>
			<td style={{...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
				{doc.total_amount ? fmt_amount(doc.total_amount) : ''}
			</td>
			<td style={tdStyle}>{doc.ccy}</td>
			<td style={tdStyle}>{doc.exported_at}</td>
			<td style={tdStyle}></td>
		</tr>
	)
}

function BatchCopyBox({results}: {results: GeneratedDoc[]}) {
	const toast = useToast()
	return (
		<div style={{background: '#fafafa', border: '1px solid #e5e5e5', borderRadius: 8, padding: '8px 12px'}}>
			<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
				<span style={dimStyle}>Export-batch ({results.length} rader)</span>
				<IconButton
					icon={<><Icon icon="tabler:clipboard-copy" size={14} /><span style={{marginLeft: 4, fontSize: 12}}>Kopiera</span></>}
					title="Kopiera TSV till urklipp"
					variant="outline"
					size="md"
					onClick={(e) => {
						const now_iso = new Date().toISOString().slice(0, 10)
						const tsv = results.map(r =>
							`${r.short}\t${now_iso}\t${r.author}\t${Math.round(r.net_total * 100) / 100}\t${r.ccy}\t${r.line_count}\t${r.short}.pdf`).join('\n')
						navigator.clipboard.writeText(tsv)
						toast.show({text: 'Kopierad till urklipp', variant: 'pop', event: e})
					}}
				/>
			</div>
		</div>
	)
}
